/**
 * Mock data for the YaaS Holders page — end-users with a client card
 * who hold and spend USDPT onchain. Mirrors the Agents
 * roster shape, but tuned for cardholders: each holder carries a masked
 * card number (the credit-card glyph itself), an approved corridor, the
 * USDPT they have spent this period, and the rewards earned against it.
 * Holders carry an explicit `qualifiedProgramIds` link to the programs
 * they qualify for, so the master-detail pane can render the active
 * "Programs" relationship. All amounts are USDPT.
 *
 * Differences vs Agents: no public ID# is surfaced; "Amount Settled"
 * becomes "Amount Spent" (`spent`); a masked `cardNumber` takes the role
 * the ID# played in the identity header.
 */

import { type Program, PROGRAMS } from "../shared/programMocks";

export type WalletStatus = "activated" | "not_activated";

/**
 * Attribution outcome — whether the holder's spend activity qualifies
 * for program rewards.
 */
export type EligibilityStatus = "threshold_met" | "below_threshold" | "not_enrolled";

export interface Holder {
    /** Internal key only — never surfaced as a public "#" identifier. */
    id: string;
    name: string;
    corridor: string;
    /** Masked credit-card number, e.g. "•••• •••• •••• 4291". */
    cardNumber: string;
    walletStatus: WalletStatus;
    /** USDPT spent in the current program period. */
    spent: number;
    /** Count of weekend / after-hours card spends (bonus condition). */
    weekendSpends: number;
    /** Rewards earned so far this period, in USDPT. */
    rewards: number;
    eligibility: EligibilityStatus;
    /** Ids of the programs this holder qualifies for (cross-domain link). */
    qualifiedProgramIds: string[];
}

/** The spend threshold (USDPT) the program rewards crossing. */
export const SPEND_THRESHOLD = 2000;

const PROGRAM_PHILIPPINES = "agent-usdpt-settlement-accelerator";
const PROGRAM_BOLIVIA = "bolivia-agent-wallet-activation-drive";
const PROGRAM_US = "us-usdpt-settlement-rebate";
const PROGRAM_BOLIVIA_ENDED = "bolivia-q1-remittance-rewards";

/** Build a masked card number from a deterministic 4-digit suffix. */
function maskCard(last4: string): string {
    return `•••• •••• •••• ${last4}`;
}

// ─── Bulk holder generation ────────────────────────────────────────────
// Deterministic filler holders so the live + ended programs have realistic
// rosters. Names are built from corridor-specific given + family name pools.
const CORRIDOR_LABEL: Record<string, string> = {
    "united-states": "United States",
    bolivia: "Bolivia",
    mexico: "Mexico"
};

const GEN_NAME_POOLS: Record<string, { given: string[]; family: string[] }> = {
    "united-states": {
        given: [
            "James",
            "Mary",
            "Robert",
            "Patricia",
            "John",
            "Jennifer",
            "Michael",
            "Linda",
            "David",
            "Barbara",
            "William",
            "Susan"
        ],
        family: ["Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia"]
    },
    bolivia: {
        given: [
            "Mateo",
            "Sofía",
            "Diego",
            "Valentina",
            "Santiago",
            "Camila",
            "Sebastián",
            "Lucía",
            "Tomás",
            "Isabela",
            "Joaquín",
            "Antonella"
        ],
        family: ["Mamani", "Quispe", "Flores", "Choque", "Condori", "Vargas"]
    },
    mexico: {
        given: ["Alejandro", "Ximena", "Emiliano", "Renata", "Daniel", "Regina"],
        family: ["Hernández", "García", "Martínez", "López"]
    }
};

function genName(corridor: string, n: number): string {
    const pool = GEN_NAME_POOLS[corridor] ?? GEN_NAME_POOLS.mexico;
    const given = pool.given[n % pool.given.length];
    const family = pool.family[Math.floor(n / pool.given.length) % pool.family.length];
    return `${given} ${family}`;
}

/** Build the bulk filler holders distributed across the live + ended programs. */
function generateHolders(): Holder[] {
    const plan: {
        count: number;
        corridor: string;
        programIds: string[];
        enrolled: boolean;
    }[] = [
        { count: 22, corridor: "united-states", programIds: [PROGRAM_US], enrolled: true },
        { count: 14, corridor: "bolivia", programIds: [PROGRAM_BOLIVIA], enrolled: true },
        { count: 9, corridor: "bolivia", programIds: [PROGRAM_BOLIVIA_ENDED], enrolled: true },
        { count: 5, corridor: "mexico", programIds: [], enrolled: false }
    ];

    const holders: Holder[] = [];
    const nameCounter: Record<string, number> = {};
    let idx = 0;

    for (const group of plan) {
        for (let i = 0; i < group.count; i++) {
            const n = nameCounter[group.corridor] ?? 0;
            nameCounter[group.corridor] = n + 1;
            const spent = group.enrolled ? (600 + ((idx * 1373) % 23000)) * 100 : 0;
            // Deterministic, varied last-4 digits derived from the index.
            const last4 = String(1000 + ((idx * 4099) % 9000));
            holders.push({
                id: String(7100 + idx),
                name: genName(group.corridor, n),
                corridor: CORRIDOR_LABEL[group.corridor] ?? group.corridor,
                cardNumber: maskCard(last4),
                walletStatus: group.enrolled ? "activated" : "not_activated",
                spent,
                weekendSpends: group.enrolled ? idx % 7 : 0,
                rewards: group.enrolled ? Math.round(spent * 0.016 * 100) / 100 : 0,
                eligibility: !group.enrolled
                    ? "not_enrolled"
                    : spent >= SPEND_THRESHOLD
                      ? "threshold_met"
                      : "below_threshold",
                qualifiedProgramIds: group.programIds
            });
            idx += 1;
        }
    }
    return holders;
}

const CURATED_HOLDERS: Holder[] = [
    {
        id: "4471",
        name: "Maria Santos",
        corridor: "Philippines",
        cardNumber: maskCard("4291"),
        walletStatus: "activated",
        spent: 8420,
        weekendSpends: 6,
        rewards: 142.1,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "3120",
        name: "Joshua Reyes",
        corridor: "Philippines",
        cardNumber: maskCard("8137"),
        walletStatus: "activated",
        spent: 5310,
        weekendSpends: 3,
        rewards: 79.65,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "2208",
        name: "Angelica Cruz",
        corridor: "Philippines",
        cardNumber: maskCard("0562"),
        walletStatus: "activated",
        spent: 12940,
        weekendSpends: 9,
        rewards: 226.45,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "5093",
        name: "Mark Dela Cruz",
        corridor: "Philippines",
        cardNumber: maskCard("7720"),
        walletStatus: "activated",
        spent: 1850,
        weekendSpends: 2,
        rewards: 34.25,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "6640",
        name: "Christine Bautista",
        corridor: "Philippines",
        cardNumber: maskCard("3318"),
        walletStatus: "activated",
        spent: 640,
        weekendSpends: 0,
        rewards: 25,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_PHILIPPINES]
    },
    {
        id: "9301",
        name: "Carlos Mamani",
        corridor: "Bolivia",
        cardNumber: maskCard("6045"),
        walletStatus: "activated",
        spent: 6120,
        weekendSpends: 2,
        rewards: 98.4,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "9355",
        name: "Lucía Vargas",
        corridor: "Bolivia",
        cardNumber: maskCard("1184"),
        walletStatus: "activated",
        spent: 3480,
        weekendSpends: 1,
        rewards: 61.2,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "9412",
        name: "Diego Flores",
        corridor: "Bolivia",
        cardNumber: maskCard("9907"),
        walletStatus: "activated",
        spent: 1240,
        weekendSpends: 0,
        rewards: 50,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_BOLIVIA]
    },
    {
        id: "5520",
        name: "David Garcia",
        corridor: "United States",
        cardNumber: maskCard("2076"),
        walletStatus: "activated",
        spent: 18750,
        weekendSpends: 5,
        rewards: 281.25,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "5588",
        name: "Jennifer Brown",
        corridor: "United States",
        cardNumber: maskCard("4453"),
        walletStatus: "activated",
        spent: 9640,
        weekendSpends: 3,
        rewards: 144.6,
        eligibility: "threshold_met",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "5601",
        name: "Robert Jones",
        corridor: "United States",
        cardNumber: maskCard("8819"),
        walletStatus: "activated",
        spent: 4200,
        weekendSpends: 0,
        rewards: 63,
        eligibility: "below_threshold",
        qualifiedProgramIds: [PROGRAM_US]
    },
    {
        id: "1187",
        name: "Renata López",
        corridor: "Mexico",
        cardNumber: maskCard("5230"),
        walletStatus: "activated",
        spent: 4180,
        weekendSpends: 1,
        rewards: 62.7,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "0912",
        name: "Arjun Mehta",
        corridor: "India",
        cardNumber: maskCard("7641"),
        walletStatus: "activated",
        spent: 3025,
        weekendSpends: 4,
        rewards: 51.4,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "7714",
        name: "Patricia Aquino",
        corridor: "Philippines",
        cardNumber: maskCard("0098"),
        walletStatus: "not_activated",
        spent: 0,
        weekendSpends: 0,
        rewards: 0,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    },
    {
        id: "8852",
        name: "Emiliano Martínez",
        corridor: "Mexico",
        cardNumber: maskCard("3375"),
        walletStatus: "not_activated",
        spent: 0,
        weekendSpends: 0,
        rewards: 0,
        eligibility: "not_enrolled",
        qualifiedProgramIds: []
    }
];

/** Spend volumes are quarterly cardholder throughput — scaled up so reward
 *  math (≈1.6% of spent) lands at program-budget magnitudes. */
const SPENT_SCALE = 100;

/**
 * Sim-only qualified holders for the seeded client-run program
 * `client-run-program-seed-1` ("USDv Cashback Rewards", Active). These give
 * that one program a populated `QualifiedParticipants` table + non-zero
 * KeyStats (TVL / Held / Distributed / Users) in `?sim=1`, without altering
 * any other program's roster. Amounts are already at final magnitude
 * (USDv-scale) so they are appended AFTER the curated-scale pass — no double
 * scaling. Program id must match mockApi's `client-run-program-seed-<n>` uuid.
 */
const SEED_CLIENT_RUN_PROGRAM_1 = "client-run-program-seed-1";
const SEEDED_PROGRAM_HOLDERS: Holder[] = [
    {
        id: "seed1-8801",
        name: "Ava Thompson",
        corridor: "United States",
        cardNumber: maskCard("6612"),
        walletStatus: "activated",
        spent: 742000,
        weekendSpends: 5,
        rewards: 11872.4,
        eligibility: "threshold_met",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8802",
        name: "Noah Bennett",
        corridor: "United States",
        cardNumber: maskCard("2048"),
        walletStatus: "activated",
        spent: 468500,
        weekendSpends: 3,
        rewards: 7496.0,
        eligibility: "threshold_met",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8803",
        name: "Mia Rodriguez",
        corridor: "United States",
        cardNumber: maskCard("9134"),
        walletStatus: "activated",
        spent: 1284000,
        weekendSpends: 8,
        rewards: 20544.0,
        eligibility: "threshold_met",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8804",
        name: "Liam Nguyen",
        corridor: "United States",
        cardNumber: maskCard("5577"),
        walletStatus: "activated",
        spent: 305000,
        weekendSpends: 2,
        rewards: 4880.0,
        eligibility: "threshold_met",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8805",
        name: "Emma Foster",
        corridor: "United States",
        cardNumber: maskCard("3390"),
        walletStatus: "activated",
        spent: 156000,
        weekendSpends: 1,
        rewards: 2496.0,
        eligibility: "below_threshold",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8806",
        name: "Oliver Grant",
        corridor: "United States",
        cardNumber: maskCard("7205"),
        walletStatus: "activated",
        spent: 892000,
        weekendSpends: 6,
        rewards: 14272.0,
        eligibility: "threshold_met",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    },
    {
        id: "seed1-8807",
        name: "Sophia Carter",
        corridor: "United States",
        cardNumber: maskCard("4418"),
        walletStatus: "activated",
        spent: 214500,
        weekendSpends: 0,
        rewards: 3432.0,
        eligibility: "below_threshold",
        qualifiedProgramIds: [SEED_CLIENT_RUN_PROGRAM_1]
    }
];

/** Curated, hand-tuned holders (scaled) first; then ~50 generated fillers;
 *  then the seeded qualified holders for the sim client-run program. */
export const HOLDERS: Holder[] = [
    ...CURATED_HOLDERS.map((h) => ({
        ...h,
        spent: h.spent * SPENT_SCALE,
        rewards: Math.round(h.rewards * SPENT_SCALE * 100) / 100
    })),
    ...generateHolders(),
    ...SEEDED_PROGRAM_HOLDERS
];

/** Holders who qualify for a given program (cross-domain link). */
export function holdersForProgram(programId: string): Holder[] {
    return HOLDERS.filter((holder) => holder.qualifiedProgramIds.includes(programId));
}

/** Current USDPT TVL — total USDPT held by a program's holders. */
export function programAmountHeld(programId: string): number {
    return Math.round(holdersForProgram(programId).reduce((sum, h) => sum + balanceOf(h), 0));
}

/** Programs a given holder qualifies for (cross-domain link). */
export function programsForHolder(holder: Holder): Program[] {
    return PROGRAMS.filter((program) => holder.qualifiedProgramIds.includes(program.id));
}

/** Programs the holder has participated in that the detail pane surfaces —
 *  live (active) and completed (ended), but not unlaunched drafts. */
export function activeProgramsForHolder(holder: Holder): Program[] {
    return programsForHolder(holder).filter((program) => program.status !== "in_progress");
}

// ─── Derived attribution figures (demo) ──────────────────────────────────
// Computed from the seeded holder so the detail pane stays rich without
// bloating every mock entry.

// Base58 alphabet (Bitcoin/Solana) — no 0, O, I, or l.
const BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

/** Deterministic base58 string of `length` chars seeded by `seed` (xorshift). */
function pseudoBase58(seed: string, length: number): string {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    let out = "";
    while (out.length < length) {
        h ^= h << 13;
        h >>>= 0;
        h ^= h >> 17;
        h ^= h << 5;
        h >>>= 0;
        out += BASE58[h % 58];
    }
    return out;
}

/** Deterministic pseudo wallet address for a holder (Solana base58, 44 chars). */
export function walletAddressOf(holder: Holder): string {
    return pseudoBase58(holder.id, 44);
}

/** Abbreviated wallet address for compact table cells, e.g. `7xKp..9fQr`. */
export function shortAddressOf(holder: Holder): string {
    const a = walletAddressOf(holder);
    return `${a.slice(0, 4)}..${a.slice(-4)}`;
}

/** Current USDPT holding — the balance the reward policies key off. */
export function balanceOf(holder: Holder): number {
    return Math.round(holder.spent * 0.6);
}

/** Rewards accrued toward the next distribution (a portion of the period). */
export function rewardsPendingOf(holder: Holder): number {
    return Math.round(holder.rewards * 0.4 * 100) / 100;
}

/** A single rewards-distribution transaction in the holder's history. */
export interface RewardPayment {
    date: string;
    programName: string;
    balance: number;
    rewards: number;
    /** Realized APY for this distribution. */
    apy: number;
    /** Base58 transaction signature — links to solscan.io/tx/<sig>. */
    txSignature: string;
}

// ─── Rewards-distribution history (weekly cadence) ────────────────────────
// The history is generated to span the program's actual runtime at a weekly
// cadence, and to *sum exactly* to the entity's lifetime `rewards` total —
// the source of truth consumed by the dashboard, programs, and reporting
// surfaces. Each row's APY is held near the program's base APY (with mild
// deterministic jitter), and each row's balance is back-computed from its
// rewards + APY so the row math (rewards ≈ balance × apy/100 × 7/365) holds.

const REWARD_CYCLE_DAYS = 7;
const MAX_REWARD_DISTRIBUTIONS = 26;

/** The most recent distribution date the history walks backwards from. */
const REWARD_ANCHOR = { year: 2025, month: 2, day: 23 }; // Mar 23 (0-based month)
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format a Date as "MMM D" (e.g. "Mar 9"), matching the table style. */
function formatRewardDate(d: Date): string {
    return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** A deterministic float in [0, 1) seeded by a string (FNV-1a + xorshift). */
function seededUnit(seed: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < seed.length; i++) {
        h ^= seed.charCodeAt(i);
        h = Math.imul(h, 16777619) >>> 0;
    }
    h ^= h << 13;
    h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5;
    h >>>= 0;
    return (h % 100000) / 100000;
}

/** Number of weekly distributions a program of `daysElapsed` has run. */
function distributionCount(daysElapsed: number): number {
    const weeks = Math.floor(daysElapsed / REWARD_CYCLE_DAYS);
    return Math.max(1, Math.min(MAX_REWARD_DISTRIBUTIONS, weeks));
}

/** Rewards payment history for a holder — one row per weekly distribution
 *  cycle over the active program's runtime, oldest first. Sums exactly to
 *  the holder's lifetime `rewards`. Empty when the holder has no active
 *  program or no rewards yet. */
export function rewardPaymentsForHolder(holder: Holder): RewardPayment[] {
    const active = activeProgramsForHolder(holder);
    if (active.length === 0 || holder.rewards === 0) return [];
    const program = active[0];
    const baseApy = program.baseApy ?? (program.programType === "continuous" ? 6 : 8);
    const count = distributionCount(program.daysElapsed ?? 90);

    const anchor = Date.UTC(REWARD_ANCHOR.year, REWARD_ANCHOR.month, REWARD_ANCHOR.day);

    // Mild linear growth weights (earlier weeks slightly smaller), so the
    // split is realistic rather than flat — but still sums to the total.
    const weights: number[] = [];
    let weightTotal = 0;
    for (let i = 0; i < count; i++) {
        const w = count === 1 ? 1 : 0.8 + 0.4 * (i / (count - 1));
        weights.push(w);
        weightTotal += w;
    }

    const payments: RewardPayment[] = [];
    let allocated = 0;
    for (let i = 0; i < count; i++) {
        const rewards =
            i === count - 1
                ? Math.round((holder.rewards - allocated) * 100) / 100
                : Math.round(((holder.rewards * weights[i]) / weightTotal) * 100) / 100;
        allocated += rewards;

        // Per-cycle APY: base ± ~0.3, deterministic per holder + cycle.
        const apy = Math.round((baseApy + (seededUnit(`${holder.id}-apy-${i}`) - 0.5) * 0.6) * 100) / 100;
        // Back-compute the balance so the row math is self-consistent:
        // rewards ≈ balance × (apy/100) × (REWARD_CYCLE_DAYS/365).
        const balance = apy > 0 ? Math.round(rewards / ((apy / 100) * (REWARD_CYCLE_DAYS / 365))) : balanceOf(holder);

        // Walk backwards weekly from the anchor (oldest row first).
        const date = new Date(anchor - (count - 1 - i) * REWARD_CYCLE_DAYS * 86400000);

        payments.push({
            date: formatRewardDate(date),
            programName: program.name,
            balance,
            rewards,
            apy,
            txSignature: pseudoBase58(`${holder.id}-tx-${i}`, 88)
        });
    }
    return payments;
}

/** Total rewards a program has distributed to its holders so far. */
export function programRewardsDistributed(programId: string): number {
    return HOLDERS.filter((h) => h.qualifiedProgramIds.includes(programId)).reduce((sum, h) => sum + h.rewards, 0);
}
